import {
  getMaverickPolicyByRegistrationNumber,
  getOwnershipTransferList,
  postOwnershipTransferCreate,
  checkUserLoginStatus,
  type OwnershipTransferApiItem
} from "@service/apiServices";
import { fetchConfigStart, fetchConfigFailure } from "@store/slices/config";
import { setToastData } from "@store/slices/globalState";
import { store } from "@store/store";
import { redirectToLoginIfNotLoggedIn } from "@utils/index";
import Router from "next/router";

const OWNERSHIP_ACTIVE_STATUSES = new Set([
  "IN_PROGRESS",
  "PAYMENT_PENDING",
  "DOCUMENT_RE_UPLOAD"
]);

const normalizeRegNo = (s: string) =>
  String(s || "").replace(/\s+/g, "").toUpperCase();

const normalizePhoneDigits = (p: string) => String(p || "").replace(/\D/g, "");

const phoneMatches = (a: string, b: string) => {
  const da = normalizePhoneDigits(a);
  const db = normalizePhoneDigits(b);
  if (!da || !db) return false;
  const tailA = da.length > 10 ? da.slice(-10) : da;
  const tailB = db.length > 10 ? db.slice(-10) : db;
  return tailA === tailB;
};

const asOwnershipList = (raw: unknown): OwnershipTransferApiItem[] => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: OwnershipTransferApiItem[] }).data;
  }
  return [];
};

const extractUrlFromCreateResponse = (data: unknown): string | null => {
  if (data == null) return null;
  if (typeof data === "string") {
    const value = data.trim();
    if (value.startsWith("/") || value.startsWith("http")) return value;
  }
  const d = data as {
    url?: string;
    data?: { url?: string } | unknown[] | string;
  };
  if (d.url) return d.url;
  if (d.data && typeof d.data === "object" && !Array.isArray(d.data) && (d.data as { url?: string }).url) {
    return (d.data as { url: string }).url;
  }
  if (Array.isArray(d.data) && d.data[0] && (d.data[0] as { url?: string }).url) {
    return (d.data[0] as { url: string }).url;
  }
  return null;
};

const getPhoneFromUserData = (userData: Record<string, unknown>): string | null => {
  if (!userData) return null;
  const p =
    userData.phone ||
    userData.mobile ||
    userData.mobile_number ||
    userData.login_phone;
  if (p != null && String(p).trim() !== "") return String(p);
  return null;
};

const getEmailFromUserData = (userData: Record<string, unknown>): string | null => {
  if (!userData) return null;
  const email = userData.email || userData.email_id;
  if (email != null && String(email).trim() !== "") return String(email);
  return null;
};

const findActiveOwnershipRow = (
  list: OwnershipTransferApiItem[],
  resolvedPolicyId: string,
  policyEkey: string | undefined,
  userPhone: string
): OwnershipTransferApiItem | undefined => {
  return list.find((r) => {
    const policyMatches =
      r.policyId === resolvedPolicyId ||
      (!!policyEkey && r.policyId === policyEkey);
    return (
      policyMatches &&
      phoneMatches(r.phone, userPhone) &&
      OWNERSHIP_ACTIVE_STATUSES.has(r.status) &&
      !!r.url
    );
  });
};

const buildGiAppAbsoluteUrl = (pathOrUrlFromApi: string): string => {
  const pathOrUrl = String(pathOrUrlFromApi || "").trim();
  if (!pathOrUrl) {
    return "";
  }
  if (
    pathOrUrl.startsWith("http://") ||
    pathOrUrl.startsWith("https://")
  ) {
    return pathOrUrl;
  }
  const base =
    (process.env.NEXT_PUBLIC_ISNP_BASE_PATH || "/gi").replace(/\/$/, "") || "";
  const rel = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  if (typeof window === "undefined") {
    return rel;
  }
  return `${window.location.origin}${base}${rel}`;
};

const resolveRegistrationFromContext = (registrationNumberArg?: string) => {
  if (registrationNumberArg) return String(registrationNumberArg);
  const fromQuery = Router.query?.registration_number;
  if (Array.isArray(fromQuery)) return fromQuery[0] || "";
  return fromQuery || "";
};

export const fetchOwnershipTransferAndRedirect = async (args: {
  showCustomBasicLoader?: boolean;
  x_source_id?: string;
  maverick_x_source_id?: string;
  policyId?: string;
  policy_ekey?: string;
  registration_number?: string;
  phone?: string;
  email?: string | null;
  source?: string;
  apiQueryParams?: Record<string, string | number | boolean | undefined>;
} = {}) => {
  const {
    showCustomBasicLoader = true,
    x_source_id,
    maverick_x_source_id,
    policyId: policyIdArg,
    policy_ekey: policyEkeyArg,
    registration_number: registrationNumberArg,
    phone: phoneArg,
    email: emailArg,
    source: sourceArg,
    apiQueryParams
  } = args || {};

  const xOrch = x_source_id;
  const xMav = maverick_x_source_id || xOrch;

  if (showCustomBasicLoader) {
    store?.dispatch(
      fetchConfigStart({
        showBasicLoader: true,
        showLottie: false
      })
    );
  }

  const redirectTo = (relativeOrAbsolute: string) => {
    const target = buildGiAppAbsoluteUrl(relativeOrAbsolute);
    if (!target) {
      throw new Error("Invalid redirect url");
    }
    window.location.href = target;
  };

  try {
    const { isLoggedIn, userData: ud } = await checkUserLoginStatus();
    if (!isLoggedIn || !ud) {
      await redirectToLoginIfNotLoggedIn();
      return;
    }
    const userPhone = getPhoneFromUserData(ud);
    if (!userPhone) {
      throw new Error(
        "Could not read your phone number. Please try again after logging in."
      );
    }
    if (phoneArg && !phoneMatches(phoneArg, userPhone)) {
      throw new Error(
        "Logged-in user does not match this ownership transfer request."
      );
    }
    const email = getEmailFromUserData(ud) || emailArg || null;
    const registration_number = resolveRegistrationFromContext(
      registrationNumberArg
    );

    if (!policyIdArg && !normalizeRegNo(registration_number)) {
      throw new Error(
        "Pass policyId, or add registration_number to the page URL / action."
      );
    }

    let resolvedPolicyId = policyIdArg || "";
    let policyEkey: string | undefined = policyEkeyArg;

    if (!resolvedPolicyId) {
      const policyRes = await getMaverickPolicyByRegistrationNumber(
        registration_number,
        { xSourceId: xMav }
      );
      const row = policyRes.data?.[0];
      if (!row?.id) {
        throw new Error(
          policyRes.message || "No policy found for this registration number."
        );
      }
      resolvedPolicyId = row.id;
      if (policyEkey === undefined && typeof row.ekey === "string") {
        policyEkey = row.ekey;
      }
    }

    const listRaw = await getOwnershipTransferList({
      xSourceId: xOrch,
      params: apiQueryParams
    });
    const list = asOwnershipList(listRaw);

    const source =
      sourceArg ||
      (typeof window !== "undefined" ? window.location.origin : "") ||
      process.env.HOST_URL ||
      "";

    const match = findActiveOwnershipRow(
      list,
      resolvedPolicyId,
      policyEkey,
      userPhone
    );
    if (match?.url) {
      redirectTo(match.url);
      return;
    }

    const createRes = await postOwnershipTransferCreate(
      {
        policyId: resolvedPolicyId,
        phone: userPhone,
        email,
        source
      },
      { xSourceId: xOrch }
    );

    const createUrl = extractUrlFromCreateResponse(createRes);
    if (createUrl) {
      redirectTo(createUrl);
      return;
    }

    const list2 = asOwnershipList(
      await getOwnershipTransferList({
        xSourceId: xOrch,
        params: apiQueryParams
      })
    );
    const match2 = findActiveOwnershipRow(
      list2,
      resolvedPolicyId,
      policyEkey,
      userPhone
    );
    if (match2?.url) {
      redirectTo(match2.url);
      return;
    }

    throw new Error(
      "Ownership transfer was created but no redirect link was returned. Please try again in a moment."
    );
  } catch (error: unknown) {
    console.error("fetchOwnershipTransferAndRedirect:", error);
    const err = error as {
      message?: string;
      response?: { data?: { message?: string } };
    };
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Could not start ownership transfer. Please try again.";
    if (showCustomBasicLoader) {
      store?.dispatch(fetchConfigFailure(message));
    }
    store?.dispatch(
      setToastData({
        toastMessage: message,
        toastMessageType: "error"
      })
    );
  }
};

export default fetchOwnershipTransferAndRedirect;
